// Screen 12, the quality report (CR-002 CR2-060, CR2-061; J-087), at
// /practice/gaps?year&aco&tin&npi&tab&status&action&show. The year and ACO come from the shell's
// selectors; J-271 (Louis, QB3 item 9a, replacing CR-005 item 3 / J-191's "the first ACO with data"):
// "All" ACOs is every ACO in an ACO-wide user's scope, read as one report (`aco=ALL`), and a header
// change redraws the report from the URL with no reload. J-301 (QB4 item 3a): a group or practice user's
// "All" is every ACO of its own groups or practices, its own rows only. With no choice in the URL it opens on
// Consolidated, "Patients with open gaps", All practices and All clinicians (J-203). The configured tabs,
// columns and glossary are read here, where features are composed (C6). `tin`, `npi` and a numeric `tab`
// are coerced: the router parses a pasted `tab=134` as a number.
// J-209 (the owner's inbox file 06, PR D): a failed or refused read says so (item 7); an ACO code not in
// scope is "not found" (item 11); the action filter is in the URL and a new practice, clinician or tab
// clears it with the status (item 15); the workbook needs PHI access (item 16; J-367 removed the patient link).
// J-232 (the owner's inbox file 33, items 1 and 4): `?group=<groupId>` filters the report by performance
// group, for a user with ACO-wide scope; for anyone else the guard drops it before any read is sent.
// J-238 (group access): a group-scoped user always carries one of its granted groups; the guard replaces
// a missing or other group with the first granted one, and the filter lists only the grants, with no All.
// J-260 (D8): a measure tab's status chip opens the "why met / not met" pop-up for a holder of
// `acoPatients.read` with PHI access (Screen 4's permission). J-301 (QB4 item 3b): a group user too, and a
// practice user through `acoGaps.read`, each for the patients of its own list (the API refuses any other).
// Anyone without PHI access sees the chip, no button.
// J-383 (Louis, 2026-10-29): the Gap Closure Workbook button only for an account whose switch is on (`/me/access`'s
// `gapClosureWorkbook`); "Download" stays for every account that may download.
import { useCallback } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { uiDocumentSchema } from "@vbltc/schema";

import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import { AcoNotFound } from "@/features/aco-shell/components/aco-not-found";
import { ShellStatusMessage } from "@/features/aco-shell/components/shell-status-message";
import { ALL_ACOS, defaultAcoCode, resolveYear } from "@/features/aco-shell/default-aco";
import { shellStatus } from "@/features/aco-shell/shell-status";
import { WhyButton } from "@/features/aco-patients/components/why-button";
import {
  QualityReportScreen,
  type WhyTriggerProps,
} from "@/features/aco-practice/gaps/quality-report-screen";
import { reportView } from "@/features/aco-practice/gaps/report-defaults";
import { meAccessQueryOptions, readMyAccess } from "@/features/auth/api/me-access";
import { hasGroupScope } from "@/features/auth/group-scope";
import { usePermission } from "@/features/auth/use-permission";
import { usePhiAccess } from "@/features/auth/use-phi-access";
import {
  grantedGroupSearch,
  grantedGroupsFor,
  isGroupScoped,
  seesPracticeGroups,
} from "@/lib/group-access";
import { dropGroupScope, reportSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/_authenticated/practice/gaps/")({
  validateSearch: reportSearchSchema,
  // J-232 (item 4): the search check cannot see the role, so the guard drops a group the role may not
  // carry, replacing the URL before the screen draws: no read goes out with it.
  beforeLoad: async ({ context, search }) => {
    const access = await readMyAccess(context.queryClient);
    if (isGroupScoped(access)) {
      const granted = grantedGroupSearch(access.groups, search);
      // Another group's practices and clinicians differ, so a replaced group clears both.
      if (granted !== undefined)
        throw redirect({
          to: "/practice/gaps",
          search: { ...search, ...granted, tin: undefined, npi: undefined },
          replace: true,
        });
      return;
    }
    // J-366: a practice user who sees performance groups keeps the URL's group when it sees it, and a
    // group it does not see is dropped before any read, as on Quality Summary (J-278). The server serves
    // only a group it sees (403 GROUP_FILTER_NOT_ALLOWED otherwise) and narrows the rows to its practices.
    const practiceGroups = seesPracticeGroups(access);
    const foreign =
      practiceGroups &&
      search.group !== undefined &&
      !access!.groups.some((g) => g.groupId === search.group);
    const kept = foreign
      ? { ...search, group: undefined }
      : dropGroupScope(
          search,
          hasGroupScope(context.session.user.role, access?.scopeKind) || practiceGroups,
        );
    // J-301 (QB4 item 3a): a practice user's "All" is every ACO of its own practices, so a URL naming
    // none stays as it is (J-251 gave it its first ACO).
    if (kept !== undefined) throw redirect({ to: "/practice/gaps", search: kept, replace: true });
  },
  // The shell draws the permission state for a role or account the report's reads refuse (J-209).
  staticData: { screenName: "Quality Detail", requires: { acoGaps: ["read"] }, ownScope: true },
  component: QualityReportPage,
});

function QualityReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // J-275: stable, so the screen's reset of a facility the new choices leave out runs once; like a new
  // practice, a new facility clears the status and action chosen for the old one.
  const onFacilityChange = useCallback(
    (facility: string | undefined) =>
      void navigate({
        search: (prev) => ({ ...prev, facility, status: undefined, action: undefined }),
      }),
    [navigate],
  );
  const metadata = useQuery(metadataQueryOptions());
  const ui = useQuery(uiConfigQueryOptions());
  const phiAccess = usePhiAccess();
  const canOpenPatient = usePermission({ acoPatients: ["read"] }) && phiAccess;
  const canDownload = usePermission({ acoGaps: ["export"] }) && phiAccess;
  const access = useQuery(meAccessQueryOptions());
  // J-383: the account's Gap Closure Workbook switch, from the caller's own access read; until it arrives the
  // button is not drawn, so a switched-off account never sees it.
  const canDownloadGapClosure = canDownload && access.data?.gapClosureWorkbook === true;
  const groupScoped = isGroupScoped(access.data);
  // J-301 (QB4 item 3b): a practice role holds no `acoPatients`; its pop-up needs `acoGaps.read`.
  const readsGaps = usePermission({ acoGaps: ["read"] });
  const canOpenWhy =
    access.data !== undefined &&
    (canOpenPatient || (access.data.scopeKind === "practice" && readsGaps && phiAccess));
  const canOpenData = usePermission({ acoData: ["load"] });
  // J-366 (Louis, 2026-10-27): a practice user who sees performance groups filters by them here, as on
  // Quality Summary (J-278), with All (its own practices) first.
  const practiceGroups = seesPracticeGroups(access.data);
  const canFilterGroups = usePermission({ acoQuality: ["read"] }) || groupScoped || practiceGroups;
  const document = uiDocumentSchema.safeParse(ui.data?.document);
  const status = shellStatus(metadata, { data: ui.data, error: ui.error, valid: document.success });
  if (status !== "ready" || metadata.data === undefined || !document.success)
    return (
      <ShellStatusMessage
        status={status === "ready" ? "loading" : status}
        onRetry={() => void Promise.all([metadata.refetch(), ui.refetch()])}
      />
    );
  const all = search.aco === undefined || search.aco === ALL_ACOS;
  if (!all && !metadata.data.acos.some((a) => a.acoCode === search.aco))
    return (
      <AcoNotFound
        acoCode={search.aco ?? ""}
        fallback={defaultAcoCode(metadata.data.acos)}
        renderLink={(code) => (
          <Link
            to="/practice/gaps"
            search={{ year: search.year, aco: code ?? ALL_ACOS }}
            className="underline"
          >
            {code === undefined ? "Open the report" : `Open ${code}'s report`}
          </Link>
        )}
      />
    );
  const year = resolveYear(metadata.data, search.year, search.aco);
  // J-271: "All" is every ACO of the scope; J-200: with no ACO with data, the report says so.
  const anyData = defaultAcoCode(metadata.data.acos, year) !== undefined;
  const aco = all ? (anyData ? ALL_ACOS : undefined) : search.aco;
  if (aco === undefined || year === undefined)
    return (
      <p className="text-muted-foreground text-sm" data-testid="quality-report-no-aco">
        No ACO in your access has results yet.
      </p>
    );
  const report = document.data.screens.quality_report;
  // J-203 (the owner's inbox file 05): every user opens on Consolidated, patients with open gaps.
  const { tab, show } = reportView(search);
  const grantedGroups =
    groupScoped || practiceGroups
      ? grantedGroupsFor(access.data?.groups ?? [], aco, year)
      : undefined;
  // J-366: a practice user's group from another year or ACO (the header keeps the URL's group) is no
  // choice here, so no read carries it; a group user's is replaced by the guard instead.
  const group =
    !canFilterGroups ||
    (practiceGroups && !(grantedGroups ?? []).some((g) => g.groupId === search.group))
      ? undefined
      : search.group;
  // A new practice, clinician, tab or Show choice clears the status and action chosen for the old one.
  const cleared = { status: undefined, action: undefined };
  return (
    <QualityReportScreen
      scope={{
        year,
        aco,
        tin: search.tin,
        npi: search.npi,
        group,
        show,
        facility: search.facility,
      }}
      tab={tab}
      {...(grantedGroups === undefined
        ? {}
        : { grantedGroups, grantedGroupsOfferAll: practiceGroups })}
      statusLabel={search.status}
      actionLabel={search.action}
      canDownload={canDownload}
      canDownloadGapClosure={canDownloadGapClosure}
      whyTrigger={
        canOpenWhy
          ? (row: WhyTriggerProps) => <WhyButton {...row} year={year} canOpenData={canOpenData} />
          : undefined
      }
      config={{
        tabs: report.tabs,
        consolidatedColumns: report.consolidated_columns,
        measureColumns: report.measure_columns,
        glossary: document.data.glossary,
        // J-274: the Inactive reason's configured words.
        inactiveWords: document.data.text,
      }}
      onScopeChange={(patch) => void navigate({ search: { ...search, ...patch, ...cleared } })}
      // J-275: the Facility choice; the screen clears it when another filter leaves its facility out.
      onFacilityChange={onFacilityChange}
      // J-232: another group's practices and clinicians differ, so a new group clears both.
      {...(canFilterGroups
        ? {
            onGroupChange: (group: number | undefined) =>
              void navigate({
                search: { ...search, group, tin: undefined, npi: undefined, ...cleared },
              }),
          }
        : {})}
      onTabChange={(next) => void navigate({ search: { ...search, tab: next, ...cleared } })}
      onStatusChange={(next) => void navigate({ search: { ...search, status: next } })}
      onActionChange={(next) => void navigate({ search: { ...search, action: next } })}
      onShowChange={(show) => void navigate({ search: { ...search, show, ...cleared } })}
    />
  );
}
